import React from 'react';
import { FeatherX, FeatherChevronRight } from '@subframe/core';
import { WALLET_PROVIDERS, WALLET_BRANDS } from '../../lib/wallet';
import { useJoeyLogin } from '../../hooks/useJoeyLogin';
import { Dialog } from '../../ui/components/Dialog';
import { WalletBadge } from './WalletBadge';
import { WalletConnectModal } from './WalletConnectModal';
import { BetaPill } from './BetaPill';

/**
 * WalletPickerModal — choose a wallet to sign in with (multi-wallet).
 *
 * Renders wallet cards (badge + name + tagline). Xaman delegates to the caller's
 * existing flow (useXamanLogin/initiateLogin → XamanModal); Joey runs the
 * WalletConnect pairing + challenge login via useJoeyLogin, surfaced through the
 * shared WalletConnectModal so both wallets look identical.
 */
interface WalletPickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Start the Xaman sign-in flow (the caller's existing initiateLogin). */
  onXaman: () => void;
}

export const WalletPickerModal: React.FC<WalletPickerModalProps> = ({ open, onClose, onXaman }) => {
  // WalletConnect-based providers (joey-wc) can't work without a Reown project id — don't
  // offer them when it's unset, otherwise selecting Joey throws on launch.
  const wcConfigured = !!import.meta.env.VITE_REOWN_PROJECT_ID;
  const providers = Object.values(WALLET_PROVIDERS).filter(
    (p) => wcConfigured || !p.capabilities.clientSigning
  );
  const joey = useJoeyLogin();

  const handleSelect = (id: string) => {
    onClose();
    if (id === 'xaman') onXaman();
    else if (id === 'joey-wc') void joey.launch();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o: boolean) => !o && onClose()}>
        <Dialog.Content className="w-full max-w-[400px]">
          {/* Header */}
          <div className="flex w-full items-center justify-between px-6 py-4">
            <span className="text-heading-3 font-heading-3 text-default-font">Choose a wallet</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded hover:bg-neutral-100 transition-colors"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>
          <div className="w-full h-px bg-neutral-border" />

          {/* Wallet cards */}
          <div className="flex w-full flex-col gap-2 p-4">
            {providers.map((p) => {
              const brand = WALLET_BRANDS[p.id];
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSelect(p.id)}
                  className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3 text-left transition-colors hover:border-brand-primary hover:bg-neutral-50"
                >
                  <WalletBadge wallet={brand} size={40} />
                  <div className="flex flex-1 flex-col min-w-0">
                    <span className="flex items-center gap-2 text-body-bold font-body-bold text-default-font">
                      {brand.name}
                      {brand.beta && <BetaPill />}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">{brand.tagline}</span>
                  </div>
                  <FeatherChevronRight className="h-5 w-5 flex-shrink-0 text-subtext-color" />
                </button>
              );
            })}
          </div>
        </Dialog.Content>
      </Dialog>

      {/* Joey pairing / challenge — shared themed connect modal */}
      <WalletConnectModal
        open={joey.stage !== 'idle'}
        onClose={joey.cancel}
        title={joey.stage === 'authenticating' ? 'Confirm in Joey' : 'Connect Joey'}
        subtitle={
          joey.stage === 'authenticating'
            ? 'Approve the sign-in request in your Joey app'
            : 'Scan the QR code with the Joey app to connect'
        }
        wallet={WALLET_BRANDS['joey-wc']}
        qrUrl={joey.stage === 'pairing' ? joey.pairingQr ?? undefined : undefined}
        deepLink={joey.stage === 'pairing' ? joey.pairingUri ?? undefined : undefined}
        statusText={
          joey.stage === 'authenticating'
            ? 'Waiting for approval…'
            : joey.pairingQr
              ? 'Waiting for connection…'
              : 'Preparing connection…'
        }
        statusVariant="waiting"
      />
    </>
  );
};
